import { supabase } from "@foundation/altzero_base/base/utils/supabaseClient";
import type { CreateIncidentPayload, IncidentRecord } from "../types/incident.types";

const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 2000, 4000];

// Supabase/PostgreSQL error codes that are NOT transient — don't retry these
const PERMANENT_ERROR_CODES = ["23503", "42501", "42P01", "23514", "PGRST116"];

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const createIncident = async (
  payload: CreateIncidentPayload
): Promise<IncidentRecord> => {
  // Generate a stable ID client-side so retries are idempotent (no duplicates)
  const clientId = crypto.randomUUID();

  // Strip only undefined fields — null values are intentional and let PostgreSQL
  // use column defaults correctly. Prevents PGRST204 if a column hasn't been added yet.
  const insertPayload = Object.fromEntries(
    Object.entries({ id: clientId, ...payload }).filter(
      ([, v]) => v !== undefined
    )
  ) as CreateIncidentPayload & { id: string };

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    const { data, error } = await supabase
      .schema("charlie")
      .from("incident_logs")
      .insert(insertPayload)
      .select()
      .single();

    // Happy path — insert + select succeeded
    if (!error && data) {
      if (typeof data.incident_ref !== "string") {
        throw new Error("Unexpected response: incident_ref missing");
      }
      console.log("[incidentService] Incident created:", data.incident_ref);
      return data as IncidentRecord;
    }

    // Duplicate key (23505) means a previous retry already inserted this row.
    // Fetch the existing row instead of re-inserting.
    if (error?.code === "23505") {
      console.log("[incidentService] Row already exists, fetching existing record");
      const { data: existing } = await supabase
        .schema("charlie")
        .from("incident_logs")
        .select()
        .eq("id", clientId)
        .single();

      if (existing) {
        return existing as IncidentRecord;
      }
      // SELECT may fail if RLS doesn't allow read-back (pre-policy-fix).
      // Fall through to the RLS-fallback below.
    }

    // Insert succeeded but SELECT was blocked by RLS (PGRST116 = 0 rows from .single()).
    // The row IS in the database — return a partial record so the UI doesn't show an error.
    const isSelectRlsFailure =
      error?.code === "PGRST116" || error?.message?.includes("0 rows");
    if (isSelectRlsFailure) {
      console.warn(
        "[incidentService] Insert succeeded but SELECT blocked by RLS. Returning partial record."
      );
      return {
        id: clientId,
        incident_ref: "",
        session_id: payload.session_id,
        venue_id: payload.venue_id ?? null,
        user_id: payload.user_id ?? null,
        org_id: payload.org_id ?? null,
        incident_type: payload.incident_type,
        assessment_data: payload.assessment_data ?? null,
        red_flags_detected: payload.red_flags_detected,
        outcome: payload.outcome,
        geolocation: payload.geolocation ?? null,
        subject_name: payload.subject_name ?? null,
        created_at: new Date().toISOString(),
        metadata: payload.metadata ?? {},
      };
    }

    console.error(
      `[incidentService] Attempt ${attempt + 1}/${MAX_RETRIES} failed:`,
      error?.message,
      error?.code
    );

    // Don't retry permanent errors (constraint violations, RLS denied, etc.)
    if (error?.code && PERMANENT_ERROR_CODES.includes(error.code)) {
      throw new Error(error.message);
    }

    if (attempt < MAX_RETRIES - 1) {
      await sleep(RETRY_DELAYS[attempt]);
    }
  }

  throw new Error("Failed to create incident after 3 retries");
};
